import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { open_api_with_cookies } from "../../Utils/network";
import styles from "./Recipes.module.css";
import stylesPromotion from "../../Components/Card/AppPromotion.module.css";
import promotionBanner from "../../Assets/promotionBanner.svg";
import RecipePromotion from "./RecipePromotion";
import ExpertSection from "../../Components/ExpertSection/ExpertSection";
import {
  convertToReadableTime,
  toISO8601Duration,
  toSchemaText,
} from "../../Utils/helper";
import { useParams, useNavigate } from "react-router-dom";
import JoinUsForm from "../../Components/JoinUsForm/JoinUsForm";

const title = (
  <p>
    Download the <span className={stylesPromotion.highlight}>Myburgo</span>{" "}
    Mobile app today to access more{" "}
    <span className={stylesPromotion.highlight}>recipes</span>
  </p>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2 2M9 2h6" strokeLinecap="round" />
  </svg>
);

// Animated nutrition ring. `value` may be a number or a unit-bearing string
// from the API (e.g. "22 g") — we parse the number for the gauge and render a
// single clean unit so we never get "22 gg".
const RING_R = 40;
const RING_C = 2 * Math.PI * RING_R;
function NutriRing({ value, unit = "", max, color, label, sub }) {
  const num = parseFloat(value) || 0;
  const pct = Math.max(0, Math.min(1, num / max));
  const off = RING_C * (1 - pct);
  const display = `${Math.round(num)}${unit}`;
  return (
    <div className={styles.ring}>
      <svg className={styles.ringSvg} viewBox="0 0 92 92">
        <circle className={styles.ringBg} cx="46" cy="46" r={RING_R} />
        <circle
          className={styles.ringFg}
          cx="46"
          cy="46"
          r={RING_R}
          stroke={color}
          style={{ "--circ": RING_C, "--off": off }}
        />
        <g className={styles.ringTxt}>
          <text className={styles.ringNum} x="46" y="52" textAnchor="middle">
            {display}
          </text>
        </g>
      </svg>
      <span className={styles.ringLbl}>
        {label}
        <small>{sub}</small>
      </span>
    </div>
  );
}

export default function Recipes() {
  const [recipe, setRecipe] = useState("");
  const [chefDetails, setChefDetails] = useState("");
  const [nutrition, setNutrition] = useState("");
  const [ingridients, setIngridients] = useState([]);
  const [directions, setDirections] = useState([]);
  const [saved, setSaved] = useState(false);
  const { slug } = useParams();
  const navigate = useNavigate();

  const getRecipe = async () => {
    try {
      const result = await open_api_with_cookies.get(`recipe/${slug}`);
      if (result?.data) {
        setRecipe(result.data);
        setChefDetails(result.data.chef || {});
      }
    } catch (error) {
      console.error("Error fetching recipe:", error);
      setRecipe({});
      setChefDetails({});
    }
  };

  const getNutrition = async () => {
    try {
      const result = await open_api_with_cookies.get(
        `recipe/nutrition/list/?recipe=${recipe.id}`
      );
      if (result?.data?.data && result.data.data.length > 0) {
        setNutrition(result.data.data[0]);
      } else {
        setNutrition({});
      }
    } catch (error) {
      console.error("Error fetching nutrition data:", error);
      setNutrition({});
    }
  };

  const getIngridients = async () => {
    try {
      const pages = await open_api_with_cookies.get(
        `recipe/ingredients/list/?recipe=${recipe.id}&page=1`
      );
      const count = pages?.data?.max_pages || 1;

      const requests = Array.from({ length: count }, (_, i) =>
        open_api_with_cookies.get(
          `recipe/ingredients/list/?recipe=${recipe.id}&page=${i + 1}`
        )
      );

      const responses = await Promise.all(requests);
      const allIngredients = responses.flatMap(
        (response) => response?.data?.data || []
      );
      setIngridients(allIngredients);
    } catch (error) {
      console.error("Error fetching ingredients:", error);
      setIngridients([]);
    }
  };

  const getDirections = async () => {
    try {
      const result = await open_api_with_cookies.get(
        `recipe/directions/list/?recipe=${recipe.id}`
      );
      const sortedDirections =
        result?.data?.data?.sort((a, b) => a.step - b.step) || [];
      setDirections(sortedDirections);
    } catch (error) {
      console.error("Error fetching directions:", error);
      setDirections([]);
    }
  };

  const cleanText = (htmlString) => {
    return htmlString
      .replace(/<\/?pre>/g, "")
      .replace(/\r\n/g, "\n")
      .trim();
  };

  const handleShare = async () => {
    const url = `https://myburgo.com/recipe/${slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: recipe.title, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      }
    } catch (e) {
      /* user dismissed share sheet — no-op */
    }
  };

  useEffect(() => {
    getRecipe();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (recipe && recipe.id) {
        await Promise.all([getNutrition(), getIngridients(), getDirections()]);
      }
    };
    fetchAdditionalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipe.id]);

  const canonicalUrl = `https://myburgo.com/recipe/${slug}`;

  const buildRecipeSchema = () => {
    const authorName =
      `${chefDetails.first_name || ""} ${chefDetails.last_name || ""}`.trim();

    const schema = {
      "@context": "https://schema.org/",
      "@type": "Recipe",
      mainEntityOfPage: canonicalUrl,
      url: canonicalUrl,
      name: recipe.title,
      image: recipe.banner ? [recipe.banner] : undefined,
      author: authorName
        ? { "@type": "Person", name: authorName }
        : undefined,
      datePublished: recipe.created_at,
      description: recipe.description,
      prepTime: toISO8601Duration(recipe.preparation_time),
      cookTime: toISO8601Duration(recipe.cooking_time),
      recipeYield: recipe.servings ? String(recipe.servings) : "1 serving",
      recipeCategory: toSchemaText(recipe.category) || "Main Course",
      recipeCuisine: toSchemaText(recipe.cuisine),
      keywords:
        [recipe.title, toSchemaText(recipe.category), recipe.difficulty_level_str]
          .filter(Boolean)
          .join(", ") || undefined,
      nutrition: nutrition?.kCal
        ? {
            "@type": "NutritionInformation",
            calories: `${nutrition.kCal || 0} kcal`,
            fatContent: `${nutrition.fat || 0} g`,
            proteinContent: `${nutrition.protein || 0} g`,
            carbohydrateContent: `${nutrition.carbs || 0} g`,
          }
        : undefined,
      recipeIngredient: ingridients.length
        ? ingridients.map((item) => `${item.quantity} ${item.ingredients.title}`)
        : undefined,
      recipeInstructions: directions.length
        ? directions.map((item) => ({
            "@type": "HowToStep",
            name: item.head_name,
            text: cleanText(item.Directions),
            url: `${canonicalUrl}#step-${item.step}`,
            position: item.step,
          }))
        : undefined,
    };

    Object.keys(schema).forEach(
      (key) => schema[key] === undefined && delete schema[key]
    );

    return schema;
  };

  // The API's diet_type values are "veg" | "nonveg" | "contains_egg"
  const dietLabel =
    recipe.diet_type === "nonveg"
      ? "Non-veg"
      : recipe.diet_type === "contains_egg"
        ? "Contains egg"
        : "Pure Veg";
  const dietClass =
    recipe.diet_type === "nonveg"
      ? styles.nonveg
      : recipe.diet_type === "contains_egg"
        ? styles.mix
        : styles.veg;

  const prepReadable = recipe.preparation_time
    ? convertToReadableTime(recipe.preparation_time)
    : "";
  const cookReadable = recipe.cooking_time
    ? convertToReadableTime(recipe.cooking_time)
    : "";
  const category = toSchemaText(recipe.category);
  const cuisine = toSchemaText(recipe.cuisine);
  const hasAside = ingridients.length > 0 || Boolean(nutrition?.kCal);

  const statItems = [
    prepReadable && {
      ic: styles.icPeach,
      icon: <ClockIcon />,
      value: prepReadable,
      label: "Prep time",
    },
    cookReadable && {
      ic: styles.icPink,
      icon: "🍳",
      value: cookReadable,
      label: "Cook time",
    },
    {
      ic: styles.icLav,
      icon: "🍽️",
      value: recipe.servings || "1",
      label: "Servings",
    },
    recipe.difficulty_level_str && {
      ic: styles.icButter,
      icon: "📊",
      value: recipe.difficulty_level_str,
      label: "Difficulty",
    },
  ].filter(Boolean);

  // Quick facts shown in the aside when there's no nutrition data to display
  const factItems = [
    { label: "Diet", value: dietLabel },
    category && { label: "Category", value: category },
    cuisine && { label: "Cuisine", value: cuisine },
    recipe.difficulty_level_str && {
      label: "Difficulty",
      value: recipe.difficulty_level_str,
    },
    prepReadable && { label: "Prep time", value: prepReadable },
    recipe.servings && { label: "Servings", value: String(recipe.servings) },
  ].filter(Boolean);

  return (
    <div className={styles.page}>
      <Helmet>
        <title>
          {recipe.title
            ? `${recipe.title} Recipe | Myburgo`
            : "Delicious Recipes | Myburgo"}
        </title>
        <meta
          name="description"
          content={
            recipe.description
              ? recipe.description.substring(0, 160)
              : "Discover delicious recipes from top chefs at Myburgo. Learn how to prepare tasty meals with step-by-step instructions."
          }
        />
        <meta
          name="keywords"
          content={`${recipe.title}, recipe, cooking, food, chef, ${recipe.difficulty_level_str}, ${chefDetails.first_name} ${chefDetails.last_name}`}
        />

        {/* Open Graph tags for social sharing */}
        <meta
          property="og:title"
          content={
            recipe.title
              ? `${recipe.title} Recipe | Myburgo`
              : "Delicious Recipes | Myburgo"
          }
        />
        <meta
          property="og:description"
          content={
            recipe.description
              ? recipe.description.substring(0, 160)
              : "Discover delicious recipes from top chefs at Myburgo"
          }
        />
        {recipe.banner && <meta property="og:image" content={recipe.banner} />}
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />

        {/* Twitter Card data */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            recipe.title
              ? `${recipe.title} Recipe | Myburgo`
              : "Delicious Recipes | Myburgo"
          }
        />
        <meta
          name="twitter:description"
          content={
            recipe.description
              ? recipe.description.substring(0, 160)
              : "Discover delicious recipes from top chefs at Myburgo"
          }
        />
        {recipe.banner && <meta name="twitter:image" content={recipe.banner} />}

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* JSON-LD structured data for recipes */}
        {recipe.id && (
          <script type="application/ld+json">
            {JSON.stringify(buildRecipeSchema())}
          </script>
        )}
      </Helmet>

      {/* ambient blobs */}
      <div className={styles.ambient} aria-hidden="true">
        <span className={`${styles.blob} ${styles.blob1}`} />
        <span className={`${styles.blob} ${styles.blob2}`} />
        <span className={`${styles.blob} ${styles.blob3}`} />
      </div>

      <div className={styles.wrap}>
        {/* breadcrumb */}
        <nav className={styles.crumbs} aria-label="Breadcrumb">
          <span
            className={styles.crumbLink}
            role="link"
            tabIndex={0}
            onClick={() => navigate("/")}
            onKeyDown={(e) => e.key === "Enter" && navigate("/")}
          >
            Home
          </span>
          <span className={styles.sep}>›</span>
          <span
            className={styles.crumbLink}
            role="link"
            tabIndex={0}
            onClick={() => navigate("/recipe")}
            onKeyDown={(e) => e.key === "Enter" && navigate("/recipe")}
          >
            Recipes
          </span>
          <span className={styles.sep}>›</span>
          <span className={styles.here}>{recipe.title}</span>
        </nav>

        {/* ───────── HERO ───────── */}
        <section className={styles.dhero}>
          <div>
            {(category || cuisine) && (
              <span className={`${styles.eyebrow} ${styles.dheroCat}`}>
                {[category, cuisine].filter(Boolean).join(" · ")}
              </span>
            )}
            <h1 className={styles.dheroTitle}>{recipe.title}</h1>
            {recipe.description && (
              <p className={styles.dheroDesc}>{recipe.description}</p>
            )}
            <div className={styles.dheroPills}>
              <span className={styles.pill}>
                <span className={`${styles.dietDot} ${dietClass}`} /> {dietLabel}
              </span>
              {recipe.difficulty_level_str && (
                <span className={styles.pill}>📊 {recipe.difficulty_level_str}</span>
              )}
              {recipe.is_ai_generated && (
                <span className={styles.pill}>🤖 AI-assisted</span>
              )}
            </div>
            <div className={styles.dheroActions}>
              <a href="#directions" className={styles.btnWarm}>
                Start cooking ↓
              </a>
              <button
                type="button"
                className={`${styles.btnGhost} ${saved ? styles.btnSaved : ""}`}
                onClick={() => setSaved((s) => !s)}
                aria-pressed={saved}
              >
                {saved ? "♥ Saved" : "♡ Save"}
              </button>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={handleShare}
              >
                ↗ Share
              </button>
            </div>
            {(chefDetails?.first_name || chefDetails?.last_name) && (
              <div
                className={styles.dheroChef}
                onClick={() => navigate(`/experts/${chefDetails.slug}`)}
                role="link"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" && navigate(`/experts/${chefDetails.slug}`)
                }
              >
                {chefDetails.image ? (
                  <img src={chefDetails.image} alt="" />
                ) : (
                  <span className={styles.chefFallback} />
                )}
                <div>
                  <b>
                    {chefDetails.prefix ? `${chefDetails.prefix} ` : ""}
                    {chefDetails.first_name} {chefDetails.last_name}
                  </b>
                  {recipe.view_count >= 0 && (
                    <small>{recipe.view_count || 0} views</small>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={styles.dheroMedia}>
            <div className={styles.photo}>
              {recipe.banner ? (
                <img src={recipe.banner} alt={recipe.title} />
              ) : (
                <span className={styles.photoFallback} aria-hidden="true" />
              )}
            </div>
            {prepReadable && (
              <div className={`${styles.floatBadge} ${styles.floatTl}`}>
                <span className={`${styles.bIc} ${styles.icPeach}`}>
                  <ClockIcon />
                </span>
                <div>
                  <b>{prepReadable}</b>
                  <small>Prep time</small>
                </div>
              </div>
            )}
            {nutrition?.kCal ? (
              <div className={`${styles.floatBadge} ${styles.floatBr}`}>
                <span className={`${styles.bIc} ${styles.icMint}`}>🔥</span>
                <div>
                  <b>{nutrition.kCal} kcal</b>
                  <small>per serving</small>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {/* ───────── QUICK STATS ───────── */}
        <section className={styles.stats}>
          {statItems.map((s) => (
            <div className={styles.stat} key={s.label}>
              <span className={`${styles.sIc} ${s.ic}`}>{s.icon}</span>
              <div>
                <b>{s.value}</b>
                <small>{s.label}</small>
              </div>
            </div>
          ))}
        </section>

        {/* ───────── BODY ───────── */}
        <div className={`${styles.dbody} ${hasAside ? "" : styles.dbodyFull}`}>
          {hasAside && (
            <aside className={styles.aside}>
              {ingridients.length > 0 && (
                <div className={styles.panel}>
                  <h3 className={styles.panelHead}>
                    Ingredients
                    {recipe.servings && (
                      <span className={styles.tag}>for {recipe.servings}</span>
                    )}
                  </h3>
                  {ingridients.map((element, index) => (
                    <div className={styles.ing} key={index}>
                      {element.ingredients?.image ? (
                        <img
                          className={styles.ingImg}
                          src={element.ingredients.image}
                          alt={element.ingredients.title}
                          loading="lazy"
                        />
                      ) : (
                        <span className={styles.ingImg} />
                      )}
                      <div className={styles.ingText}>
                        <span className={styles.ingName}>
                          {element.ingredients?.title}
                        </span>
                        {element.help_text && (
                          <span className={styles.ingHint}>
                            {element.help_text}
                          </span>
                        )}
                      </div>
                      <span className={styles.ingQty}>{element.quantity}</span>
                    </div>
                  ))}
                </div>
              )}

              {!nutrition?.kCal && factItems.length > 0 && (
                <div className={styles.panel}>
                  <h3 className={styles.panelHead}>Good to know</h3>
                  <ul className={styles.facts}>
                    {factItems.map((f) => (
                      <li key={f.label}>
                        <span>{f.label}</span>
                        <b>{f.value}</b>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {nutrition?.kCal ? (
                <div className={styles.panel}>
                  <h3 className={styles.panelHead}>
                    Nutrition
                    <span className={styles.tag}>per serving</span>
                  </h3>
                  <div className={styles.nutri}>
                    <NutriRing
                      value={nutrition.kCal || 0}
                      max={500}
                      color="#FF8A3D"
                      label="Calories"
                      sub="kcal"
                    />
                    <NutriRing
                      value={nutrition.protein || 0}
                      unit="g"
                      max={30}
                      color="#7A53FF"
                      label="Protein"
                      sub="builds muscle"
                    />
                    <NutriRing
                      value={nutrition.fat || 0}
                      unit="g"
                      max={30}
                      color="#FF67A1"
                      label="Fat"
                      sub="healthy fats"
                    />
                    <NutriRing
                      value={nutrition.carbs || 0}
                      unit="g"
                      max={60}
                      color="#34C77B"
                      label="Carbs"
                      sub="slow energy"
                    />
                  </div>
                </div>
              ) : null}
            </aside>
          )}

          {/* directions timeline */}
          <section id="directions" className={styles.directions}>
            <div className={styles.dirHead}>
              <h2 className={styles.dirTitle}>How to make it</h2>
              {directions.length > 0 && (
                <span className={styles.pill}>{directions.length} steps</span>
              )}
            </div>

            {directions.length > 0 ? (
              <div className={styles.timeline}>
                {directions.map((element) => (
                  <div className={styles.step} key={element.step}>
                    <div className={styles.stepNum}>{element.step}</div>
                    <div className={styles.stepCard}>
                      {element.head_name && (
                        <h4 className={styles.stepHead}>{element.head_name}</h4>
                      )}
                      <p className={styles.stepDesc}>
                        {cleanText(element.Directions)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.dirEmpty}>Directions coming soon.</p>
            )}

            {recipe.is_ai_generated ? (
              <div className={styles.aiNote}>
                ⚠️ This AI-generated recipe may contain inaccuracies. Always check
                ingredients for allergens and consult your paediatrician before
                introducing new foods.
              </div>
            ) : null}
          </section>
        </div>
      </div>

      <RecipePromotion banner={promotionBanner} title={title} />

      {chefDetails.id != 10 ? (
        <section className={styles.expert_bg}>
          <ExpertSection expertIds={[chefDetails.id]} />
        </section>
      ) : null}

      <div style={{ margin: "50px 0" }}>
        <JoinUsForm />
      </div>
    </div>
  );
}
